import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LogIn, Eye, EyeOff, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const loadingTimerRef = React.useRef(null);

  // Shown when the app logged the user out because their login had expired
  useEffect(() => {
    if (sessionStorage.getItem('sessionExpired')) {
      sessionStorage.removeItem('sessionExpired');
      toast.info('Your session expired. Please log in again.');
    }
  }, []);
  const loadingMessages = [
    { delay: 0,     msg: 'Signing you in...' },
    { delay: 5000,  msg: 'Knocking on the server door... 🚪' },
    { delay: 12000, msg: 'The shopkeeper was resting — waking him up! ☕' },
    { delay: 20000, msg: 'He\'s putting on his shoes... almost ready! 👟' },
    { delay: 30000, msg: 'Shop is opening! Just unlocking the door now... 🔑' },
    { delay: 45000, msg: 'He\'s here! Counter is being set up — 10 more seconds! 🏪' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoadingMessage('Signing you in...');
    // Progressive messages during cold start
    const timers = [];
    loadingMessages.forEach(({ delay, msg }) => {
      if (delay > 0) {
        timers.push(setTimeout(() => setLoadingMessage(msg), delay));
      }
    });
    loadingTimerRef.current = timers;

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      
      toast.success('Login successful!');
      onLogin(response.data.access_token, response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
      setLoadingMessage('');
      if (loadingTimerRef.current) {
        loadingTimerRef.current.forEach(t => clearTimeout(t));
      }
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Form */}
      <div className="w-full lg:w-2/5 flex items-center justify-center p-8 bg-white relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-teal-500/10 to-emerald-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-emerald-500/10 to-teal-500/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="w-full max-w-md space-y-8 relative z-10">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-teal-700 to-emerald-400">
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-teal-700 to-emerald-500 bg-clip-text text-transparent" style={{ fontFamily: 'Manrope, sans-serif' }}>
                VoiceCRM
              </h1>
            </div>
            <p className="text-base text-slate-600">
              Sign in to manage your sales pipeline
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-semibold text-slate-700">
                Email ID
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 rounded-xl bg-white transition-all shadow-sm"
                data-testid="email-input"
                placeholder="your.name@synapsehealthtech.in"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-semibold text-slate-700">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 rounded-xl bg-white transition-all shadow-sm pr-12"
                  data-testid="password-input"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-teal-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-teal-700 to-emerald-500 hover:from-teal-800 hover:to-emerald-600 text-white shadow-lg shadow-teal-500/25 rounded-xl h-12 font-semibold transition-all active:scale-[0.98]"
              data-testid="login-submit-button"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-5 w-5 flex-shrink-0" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {loadingMessage || 'Signing you in...'}
                </span>
              ) : (
                <>
                  <LogIn className="mr-2 h-5 w-5" />
                  Sign In
                </>
              )}
            </Button>
          </form>

          {/* Cold start notice — appears after 12 seconds */}
          {loading && (loadingMessage.includes('shopkeeper') || loadingMessage.includes('shoes') || loadingMessage.includes('unlocking') || loadingMessage.includes('Counter')) && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-center">
              <p className="text-sm font-semibold text-amber-700">
                🏪 Our shopkeeper was taking a nap!
              </p>
              <p className="text-xs text-amber-600 mt-2 leading-relaxed">
                Think of our server like a shopkeeper. When no one visits for
                15 minutes, he locks up and rests. The <strong>first knock of
                the day</strong> wakes him up — about 30 seconds.
                Once he's up, <strong>every login after this will be instant!</strong> ⚡
              </p>
            </div>
          )}

          <div className="text-center text-sm text-slate-500 bg-slate-50 rounded-xl p-4">
            <p>Contact your administrator if you need access or forgot your password.</p>
          </div>
        </div>
      </div>

      {/* Right Side - Hero Image */}
      <div
        className="hidden lg:block lg:w-3/5 relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1576091160550-2173dba999ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxkZW50YWwlMjBjbGluaWN8ZW58MHx8fHwxNzM3OTg0NTI1fDA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-teal-800/90 via-teal-700/70 to-emerald-500/80"></div>
        
        {/* Content */}
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="max-w-lg space-y-6 text-white">
            <h2 className="text-5xl font-bold leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Empower Your Sales Team
            </h2>
            <p className="text-xl text-white/90 leading-relaxed">
              Track accounts, manage relationships, and never miss a follow-up with VoiceCRM's intelligent sales management platform.
            </p>
            <div className="flex items-center gap-4 pt-4">
              <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium">Live Dashboard</span>
              </div>
              <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2">
                <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium">Smart Reminders</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
